import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import apiCall from "../../../../../API/apiService";
import apiEndpoints from "../../../../../API/apiEndpoints";
import { toasty } from "../../../../../../Helper";

const CSVUploadDrawer = ({ onClose, job_opening_id, data, updateRoundDetails }) => {
  const [csvType, setCsvType] = useState('');
  const [numQuestionsType, setNumQuestionsType] = useState('all');
  const [customNumQuestions, setCustomNumQuestions] = useState('');
  const [numQuestions, setNumQuestions] = useState('all');
  const [file, setFile] = useState(null);
  const [roundNumber, setRoundNumber] = useState('');
  const [saveError, setSaveError] = useState('');
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleCsvTypeChange = (event) => {
    setCsvType(event.target.value);
  };

  const handleNumQuestionsTypeChange = (event) => {
    const value = event.target.value;
    setNumQuestionsType(value);
    if (value === 'all') {
      setNumQuestions('all');
    } else {
      setNumQuestions(customNumQuestions);
    }
  };

  const handleCustomNumQuestionsChange = (event) => {
    const value = event.target.value;
    setCustomNumQuestions(value);
    if (numQuestionsType === 'custom') {
      setNumQuestions(value);
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const fileType = selectedFile.type;
      const validTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      if (validTypes.includes(fileType) || selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
      } else {
        toasty("Please upload a valid CSV or Excel file", "error");
        setFile(null);
      }
    }
  };

  const handleRoundNumberChange = (event) => {
    setRoundNumber(event.target.value);
  };

  const areAllFieldsFilled = () => {
    return csvType && file && roundNumber && 
           (numQuestionsType === 'all' || (numQuestionsType === 'custom' && customNumQuestions));
  };

  const handleSave = async () => {
    setIsUploading(true);
    setUploadProgress(0);
    
    setSaveError('');
    if (!areAllFieldsFilled()) {
      setSaveError('Please fill all required fields');
      return;
    }

    const formData = new FormData();
    formData.append('csv_file', file);
    formData.append('jobopening_id', job_opening_id);
    formData.append('round_no', roundNumber);
    formData.append('num_questions', numQuestions);
    formData.append('csv_type', csvType);
    
    // Add question types
    

    try {
      // Make API call with increased timeout and progress tracking
      const response = await apiCall("post", apiEndpoints.UPLOAD_QUESTIONS, formData, {
        timeout: 120000,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      console.log("Upload response:", response);
      toasty("File uploaded successfully", "success");
      updateRoundDetails(job_opening_id);
      onClose();
    } catch (error) {
      console.error("Error uploading file:", error);
      
      // Handle different types of errors
      let errorMessage = "Error uploading file";
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = "Upload timed out. Please try with a smaller file or check your connection.";
      } else if (error.response && error.response.data) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      }
      
      setSaveError(errorMessage);
      toasty(errorMessage, "error");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  return (
    <Box
      sx={{
        width: 350,
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Upload CSV</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      {/* CSV Type */}
      <Box mb={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Select CSV Type*
        </Typography>
        <Select
          value={csvType}
          onChange={handleCsvTypeChange}
          fullWidth
          displayEmpty
          required
        >
          <MenuItem value="" disabled>Select Type</MenuItem>
          <MenuItem value="QA">Question & Answer</MenuItem>
          <MenuItem value="QAE">QA & Evaluation</MenuItem>
          <MenuItem value="FRAMEWORK">FRAMEWORK</MenuItem>
        </Select>
      </Box>

      {/* Upload CSV */}
      <Box mb={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Upload CSV*
        </Typography>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        />
        <Button
          variant="outlined"
          component="span"
          startIcon={<CloudUploadIcon />}
          fullWidth
          onClick={handleButtonClick}
          sx={{
            height: '56px',
            border: '1px solid rgba(0, 0, 0, 0.23)',
            color: file ? 'text.primary' : 'text.secondary',
            textTransform: 'none',
            justifyContent: 'flex-start',
            paddingLeft: '14px',
          }}
        >
          {file ? file.name : "Choose CSV or Excel File"}
        </Button>
      </Box>

      {/* Select Round */}
      <Box mb={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Select Round*
        </Typography>
        <Select
          value={roundNumber}
          onChange={handleRoundNumberChange}
          fullWidth
          displayEmpty
          required
        >
          <MenuItem value="" disabled>Select Round Number</MenuItem>
          {data && [...Array(data.total_rounds)].map((_, index) => (
            <MenuItem key={index} value={index + 1}>
              Round {index + 1}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Number of Questions */}
      <Box mb={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
          Select Number of Questions*
        </Typography>
        <RadioGroup
          value={numQuestionsType}
          onChange={handleNumQuestionsTypeChange}
        >
          <FormControlLabel value="all" control={<Radio />} label="All" />
          <FormControlLabel value="custom" control={<Radio />} label="Custom" />
        </RadioGroup>
        {numQuestionsType === 'custom' && (
          <TextField
            type="number"
            value={customNumQuestions}
            onChange={handleCustomNumQuestionsChange}
            fullWidth
            placeholder="Enter number of questions"
            inputProps={{ min: 1 }}
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      {/* Save and Clear Buttons */}
      <Box mt="auto">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          fullWidth
          sx={{ mb: 1, backgroundColor: "#2c5da7" }}
          disabled={!areAllFieldsFilled() || isUploading}
        >
          {isUploading ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
              {`Uploading ${uploadProgress}%`}
            </Box>
          ) : (
            'Save'
          )}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            setCsvType('');
            setNumQuestionsType('all');
            setCustomNumQuestions('');
            setFile(null);
            setRoundNumber('');
            setSaveError('');
          }}
          fullWidth
        >
          Clear
        </Button>
      </Box>

      {saveError && (
        <Typography color="error" variant="body2" mt={1}>
          {saveError}
        </Typography>
      )}
    </Box>
  );
};

export default CSVUploadDrawer;
